import { useNavigate } from "react-router-dom";
import useAppState from "../../context/AppContext";
import { APP_RETIREMENT_URLS, ERROR_MESSAGES, PSC_DATE } from "../../const/AppConst";
import StickButtonsBloc from "../common/StickButtonsBloc";
import { useForm } from "react-hook-form";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import BesoinInfoFormComponent from "../../components/retirement/besoin/BesoinInfoFormComponent";
import CoverLevelFormComponent from "../../components/retirement/besoin/CoverLevelFormComponent";
import { IRECoveragesInfo } from "../../context/model";
import { getInfoRetirementSteps, isValidDate, isWithinLast3Months, normalizeDate, parseLocalDate } from "../../utils/utils";

export type BesoinBlocFormData = {
  hasOldMutual?: string |null;
  effectiveDate?: string |null;
  test?: string;
  coveragesInfo?: IRECoveragesInfo;
  earliestDate: boolean;
  earliestDateForRecentlyRetired: boolean
}

export default function REBesoinBloc() {

  const navigate = useNavigate();
  const {
    state: { proposition, updatedProposition, infoSteps, hasActivePSC },
    dispatch
  } = useAppState();

  const validationSchema = Yup.object().shape({
    hasOldMutual: Yup.boolean().required(ERROR_MESSAGES.requiredMsg),
    effectiveDate: Yup.string().required(ERROR_MESSAGES.requiredMsg)
    .test("validDate", ERROR_MESSAGES.invalidMsg, (val)=> {
      return val !== undefined && isValidDate(val)
    })
    .test('afterDate', "La date saisie est antérieure à celle autorisée", (val) => {
      if (!val) return false;
      const isRecentlyRetired =
        updatedProposition?.retirementDate
          ? isWithinLast3Months(updatedProposition.retirementDate)
          : false;
      if (isRecentlyRetired && watch('earliestDateForRecentlyRetired') === true) {
        return true;
      }
      const today = normalizeDate(new Date());
      const pscDate = normalizeDate(new Date(PSC_DATE));
      const latestDate =
        pscDate > today
          ? pscDate
          : today;
      return parseLocalDate(val) >= latestDate;
    })
    .test("afterMutualDate", "La date saisie est antérieure à celle autorisée", (val) => {
      const isRecentlyRetired = updatedProposition?.retirementDate ? isWithinLast3Months(updatedProposition?.retirementDate!) : false;
      if (isRecentlyRetired && watch('earliestDateForRecentlyRetired') === true) return true;
      let pickedDate = new Date();
      // do not have old mutual and do not retire from last 3months, date minimum is today
      if(watch('hasOldMutual') === 'false' && !isRecentlyRetired){
        pickedDate.setDate(pickedDate.getDate()-1);
      }

      // do not have old mutual and retire from last 3months, date minimum is today
      if(watch('hasOldMutual') === 'false' && isRecentlyRetired && updatedProposition?.retirementDate){
        pickedDate.setDate(pickedDate.getDate()-1);
      }

      // do have old mutual, date minimum is today + 1 month + 2 days
      if(watch('hasOldMutual') === 'true'){
        pickedDate.setDate(pickedDate.getDate()+2);
        pickedDate.setMonth(pickedDate.getMonth()+1);
        pickedDate.setHours(0,0,0,0,);
      }
      
      return val !== undefined && isValidDate(val) && new Date(val) >= pickedDate;
    })
    .test('afterRetirementDate', "La date d'effet ne peut être antérieure à la date de départ à la retraite.", (val)=> {
      const isRecentlyRetired = updatedProposition?.retirementDate ? isWithinLast3Months(updatedProposition?.retirementDate!) : false;
      if (isRecentlyRetired && watch('earliestDateForRecentlyRetired') === true) return true;
      return (
        val!==undefined 
        && isValidDate(val) 
        && new Date(val) >= new Date(updatedProposition?.retirementDate ?? '')
      )
    })
    .test('isRetirementDate', "La date d'effet doit être la date de départ à la retraite.", (val)=> {
      const isRecentlyRetired = updatedProposition?.retirementDate ? isWithinLast3Months(updatedProposition?.retirementDate!) : false;
      if (isRecentlyRetired && watch('earliestDateForRecentlyRetired') === true) {
        return (
          val!==undefined 
          && isValidDate(val) 
          && val === updatedProposition?.retirementDate
        )
      }
      return true;
    })
  })

  const getEarliestDateForRecentlyRetiredDefaultValue = () => {
    if (updatedProposition?.retirementDate && !isWithinLast3Months(updatedProposition?.retirementDate)){
      return false;
    }
    return updatedProposition?.earliestDateForRecentlyRetired ?? false;
  }

  const hasOldMutualStatus = updatedProposition?.hasOldMutual;
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<BesoinBlocFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      hasOldMutual: hasActivePSC ? 'false' : (hasOldMutualStatus === undefined 
                    || hasOldMutualStatus === null) 
                        ? null 
                        : hasOldMutualStatus.toString(),
      effectiveDate: updatedProposition?.effectiveDate ?? null ,
      coveragesInfo: updatedProposition?.coveragesInfo ??  {
        nonRefundingPayment: 3,
        budget: 0
       
    },
      earliestDate: updatedProposition?.earliestDate ?? false,
      earliestDateForRecentlyRetired: getEarliestDateForRecentlyRetiredDefaultValue(),
    },
    resolver: yupResolver(validationSchema)
  });
  const onNextAction =  (data: BesoinBlocFormData) => {
    let updateProp =  updatedProposition;
    if(updateProp  ){
      updateProp = {
        ...updateProp, 
        ...data,
         hasOldMutual: data?.hasOldMutual?.toString() ==='true' 
      }
    }
    const nextUrl = APP_RETIREMENT_URLS.guarantyPage;

    const hasOldMutualStep = getInfoRetirementSteps
    (infoSteps, "INFO_OLD_CONTRACT", 
      data?.hasOldMutual?.toString() === "true",
      "INFO_CONTACT"
    );

    if (hasOldMutualStep.hasChanged) {
      dispatch({type: "UPDATE_STEPS", 
        steps: hasOldMutualStep.infoSteps
      })
    }
    dispatch({
      type: 'NEXT_INFO_STEP', 
      path: nextUrl, 
      updatedProposition: updateProp
    });
    navigate({
        pathname: nextUrl,
        search: `?id=${proposition!.propositionId}`,
    });         
    }

  const onPreviousAction =  () => {
    const url = APP_RETIREMENT_URLS.situationPage;
    dispatch({type: 'BACK_INFO_STEP', path: url});
    navigate({
        pathname: url,
        search: `?id=${proposition!.propositionId}`,
    });         
  }

  return (
    <form style={{width: '100%'}}>
        <div className='form-row page-content'>
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
             <BesoinInfoFormComponent 
                register={register} 
                errors={errors}  
                getValues={getValues}
                setValue={setValue}
                clearErrors={clearErrors}
                watch={watch}
              />       
          </div>         
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs' >
            <CoverLevelFormComponent 
              setValue={setValue} 
              getValues={getValues} 
            />
          </div>
        </div>
      <StickButtonsBloc 
          showPreviousButton={true} 
          disabledPreviousButton={false} 
          nextAction={handleSubmit(onNextAction)} 
          showNextButton={true} 
          previousAction={onPreviousAction}
      />
    </form>
  )
}